/**
 * Char - Web Component (iframe-based)
 *
 * A vanilla custom element that creates an iframe pointing to the SaaS app's
 * `/embed/` entrypoint. Exposes `connect()`, CSS variable theming, and DOM events
 * while keeping host and iframe concerns isolated.
 *
 * ## Authentication (Imperative API)
 *
 * Use the `connect()` method for secure authentication.
 *
 * @example Vanilla JS
 * ```js
 * const agent = document.querySelector('char-agent')
 * agent.connect({ publishableKey: 'pk_live_...' })
 * ```
 *
 * @example React
 * ```tsx
 * const agentRef = useRef<CharAgentElement>(null)
 * useEffect(() => {
 *   agentRef.current?.connect({ publishableKey: 'pk_live_...', idToken: session.idToken })
 * }, [session.idToken])
 * return <char-agent ref={agentRef} />
 * ```
 *
 * @example CSS variable theming
 * ```css
 * char-agent {
 *   --char-color-primary: #0f766e;
 *   --char-radius: 12px;
 * }
 * ```
 */

import type {
	CharErrorDetail,
	CharContainerDimensions,
	CharDeviceCapabilities,
	CharDisplayMode,
	CharHostContext,
	CharHostToIframeMessage,
	CharOpenLinkDetail,
	CharRequestDisplayModeDetail,
	CharSizeChangedDetail,
	ConnectOptions,
} from './types'

import { CharIframeProxy } from './host/char-iframe-proxy'
import { WEBMCP_PRODUCTION_API_BASE } from './utils/constants'
import { sanitizeCssVariableValue } from './utils/css-sanitizer'
import { resolveCssValue } from './utils/css-resolver'
import { CHAR_CSS_VARIABLE_NAMES } from './utils/css-variables'
import { resolveDisplayModeFromAttribute } from './utils/display-mode'
import { mergeHostContext } from './utils/host-context-merge'

/**
 * Shared options used by custom events emitted from the host element.
 */
const CHAR_CUSTOM_EVENT_OPTIONS = { bubbles: true, composed: true } as const

/**
 * Inline iframe styles used to make the host element a transparent container.
 */
const IFRAME_STYLE = 'width:100%;height:100%;border:none;display:block;'

/**
 * Display mode values advertised to the embedded runtime.
 */
const DISPLAY_MODES: readonly CharDisplayMode[] = ['inline', 'fullscreen', 'pip']

/**
 * Minimal structural shape for iframe-originated messages.
 */
interface CharIframeMessageData extends Record<string, unknown> {
	type: string
}

import { parseBooleanAttribute } from './utils/parse-boolean-attribute'

/**
 * Runtime guard for the message envelope emitted by the iframe.
 */
function isCharIframeMessageData(value: unknown): value is CharIframeMessageData {
	if (!value || typeof value !== 'object') return false
	const maybeType = (value as { type?: unknown }).type
	return typeof maybeType === 'string' && maybeType.startsWith('char-')
}

function getStringOrUndefined(value: unknown): string | undefined {
	return typeof value === 'string' ? value : undefined
}

function getNumberOrUndefined(value: unknown): number | undefined {
	return typeof value === 'number' ? value : undefined
}

export type {
	CharContainerDimensions,
	CharDeviceCapabilities,
	CharDisplayMode,
	CharErrorDetail,
	CharHostContext,
	CharHostStyles,
	CharOpenLinkDetail,
	CharPlatform,
	CharRequestDisplayModeDetail,
	CharSafeAreaInsets,
	CharSizeChangedDetail,
	CharTheme,
	ConnectOptions,
} from './types'

/**
 * Get the iframe src URL.
 * Points to the SaaS app's static /embed/ entrypoint with the parent origin as a query parameter.
 *
 * @param apiBase - Public base URL for the Char SaaS application.
 * @param parentOrigin - Origin of the host page embedding the widget.
 * @returns Fully qualified iframe URL targeting the embed entrypoint.
 */
function getIframeSrc(apiBase: string, parentOrigin: string): string {
	return `${apiBase}/embed/?parentOrigin=${encodeURIComponent(parentOrigin)}`
}

/**
 * Extract host CSS variable values from a host element.
 * Includes both `--char-*` and MCP UI style `--color-*`/`--font-*` tokens.
 * Uses a static list since getComputedStyle() cannot enumerate custom properties.
 * Values containing `var()` are resolved to concrete values before transport
 * so the iframe can safely apply them in its own CSS scope.
 *
 * @param el - Host `<char-agent>` element.
 * @returns A snapshot of resolved CSS custom properties for the iframe.
 */
function extractCharVariables(el: HTMLElement): Record<string, string> {
	const computed = getComputedStyle(el)
	const vars: Record<string, string> = {}
	for (const name of CHAR_CSS_VARIABLE_NAMES) {
		const rawValue = computed.getPropertyValue(name).trim()
		if (rawValue) {
			const resolvedValue = resolveCssValue(rawValue, computed, new Set([name]))
			const sanitizedValue = sanitizeCssVariableValue(resolvedValue || rawValue, name)
			if (sanitizedValue !== undefined) {
				vars[name] = sanitizedValue
			}
		}
	}
	return vars
}

/**
 * Detect whether the host page is in dark mode.
 * Checks the data-theme attribute, the `dark` class on <html>, and the prefers-color-scheme media query.
 *
 * @returns `true` when the host page should be treated as dark mode.
 */
function isDarkMode(): boolean {
	if (typeof document === 'undefined') return false
	const themeAttr = document.documentElement.getAttribute('data-theme')
	if (themeAttr === 'dark') return true
	if (themeAttr === 'light') return false
	return (
		document.documentElement.classList.contains('dark') ||
		window.matchMedia('(prefers-color-scheme: dark)').matches
	)
}

/**
 * Deep equality check using JSON.stringify. Sufficient for the flat/nested
 * structures in CharHostContext (no circular refs, no functions).
 *
 * @param a - First value.
 * @param b - Second value.
 * @returns `true` when both values serialize identically.
 */
function deepEqual(a: unknown, b: unknown): boolean {
	return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Parses a CSS pixel value into a finite number.
 */
function parsePxToNumber(value: string): number {
	const parsed = Number.parseFloat(value)
	return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Reads safe-area inset values using `env(safe-area-inset-*)`.
 */
function getSafeAreaInsets(): CharHostContext['safeAreaInsets'] | undefined {
	if (typeof document === 'undefined') return undefined

	const probe = document.createElement('div')
	probe.style.cssText = [
		'position:fixed',
		'visibility:hidden',
		'pointer-events:none',
		'inset:0 auto auto 0',
		'padding-top:env(safe-area-inset-top)',
		'padding-right:env(safe-area-inset-right)',
		'padding-bottom:env(safe-area-inset-bottom)',
		'padding-left:env(safe-area-inset-left)',
	].join(';')

	document.documentElement.appendChild(probe)
	const computed = getComputedStyle(probe)
	const insets = {
		top: parsePxToNumber(computed.paddingTop),
		right: parsePxToNumber(computed.paddingRight),
		bottom: parsePxToNumber(computed.paddingBottom),
		left: parsePxToNumber(computed.paddingLeft),
	}
	probe.remove()

	return insets
}

/**
 * Identifies the host platform category from the user agent.
 * Note: 'desktop' detection not yet implemented; non-mobile UAs resolve to 'web'.
 */
function detectPlatform(): 'web' | 'desktop' | 'mobile' {
	if (typeof navigator === 'undefined') return 'web'
	const ua = navigator.userAgent.toLowerCase()
	if (/(iphone|ipad|ipod|android|mobile)/.test(ua)) return 'mobile'
	return 'web'
}

/**
 * Captures lightweight host device capability hints.
 */
function getDeviceCapabilities(): CharDeviceCapabilities {
	if (typeof window === 'undefined' || typeof navigator === 'undefined') {
		return {}
	}
	return {
		touch: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
		hover: window.matchMedia('(hover: hover)').matches,
		pointerFine: window.matchMedia('(pointer: fine)').matches,
	}
}

/**
 * `<char-agent>` custom element.
 *
 * Creates an iframe pointing to the SaaS app's /embed/ entrypoint and relays
 * auth, styles, dark mode, display mode, and MCP messages via postMessage.
 *
 * Uses a unified `char-context` message with diffing (only changed fields
 * are sent) instead of separate messages per concern.
 *
 * @public
 */
class CharAgentElement extends HTMLElement {
	static observedAttributes = [
		'publishable-key',
		'enable-debug-tools',
		'display-mode',
		'api-base',
	]

	/**
	 * Publishable key for org identification.
	 * Set via `publishable-key` attribute or JS property.
	 */
	declare publishableKey?: string

	/**
	 * React 19-friendly property form of `api-base`.
	 * Read once during `connectedCallback`.
	 */
	declare apiBase?: string

	/** Active iframe element owned by this custom element instance. */
	private _iframe: HTMLIFrameElement | null = null
	/** MCP relay between host window and embed iframe. */
	private _proxy: CharIframeProxy | null = null
	/** Indicates whether the iframe has emitted `char-ready`. */
	private _iframeReady = false
	/** Bound window message handler for iframe-originated events. */
	private _messageListener: ((event: MessageEvent) => void) | null = null
	/** Observer that tracks host theme mutations. */
	private _darkModeObserver: MutationObserver | null = null
	/** Media query object for `prefers-color-scheme`. */
	private _darkModeMediaQuery: MediaQueryList | null = null
	/** Registered media query callback. */
	private _darkModeMediaHandler: (() => void) | null = null
	/** Observer that tracks host style/class mutations. */
	private _styleObserver: MutationObserver | null = null
	/** Observer that tracks container size changes. */
	private _containerResizeObserver: ResizeObserver | null = null
	/** In-flight teardown request waiting for iframe acknowledgement. */
	private _teardownPending:
		| { requestId: string; resolve: () => void; timeoutId: number }
		| null = null
	/** Monotonic sequence used to construct teardown request IDs. */
	private _teardownSequence = 0
	/** Re-entrancy guard used during disconnect lifecycle. */
	private _isDisconnecting = false

	/** Last host context snapshot used for delta emission. */
	private _hostContext: CharHostContext = {}

	/** Pending credentials that are flushed when the iframe is ready. */
	private _pendingAuth: {
		publishableKey?: string
		idToken?: string
	} | null = null

	/**
	 * Mount lifecycle: builds iframe runtime, starts proxying, and starts host observers.
	 * The existing runtime is reused when reconnecting during fast detach/reattach cycles.
	 */
	connectedCallback() {
		if (this._hasActiveRuntime()) {
			this._isDisconnecting = false
			this._teardownSequence = 0
			if (this._teardownPending) {
				window.clearTimeout(this._teardownPending.timeoutId)
				this._teardownPending.resolve()
				this._teardownPending = null
			}
			if (this._iframe && !this.contains(this._iframe)) {
				this.appendChild(this._iframe)
			}
			return
		}

		if (!this.style.display) {
			this.style.display = 'block'
		}

		const apiBase =
			this._resolveApiBaseOverride() ?? WEBMCP_PRODUCTION_API_BASE
		const iframeOrigin = this._resolveIframeOrigin(apiBase)
		const iframe = this._createIframe(apiBase)
		this._iframe = iframe

		const publishableKey = this.publishableKey ?? this.getAttribute('publishable-key') ?? undefined
		if (publishableKey) {
			this._pendingAuth = { publishableKey }
		}

		this._proxy = new CharIframeProxy(iframe, iframeOrigin)
		this._proxy.start()

		this._messageListener = this._createMessageListener(iframe, iframeOrigin)
		window.addEventListener('message', this._messageListener)

		this._observeDarkMode()
		this._observeStyleChanges()
		this._observeContainerDimensions()
		this.appendChild(iframe)
	}

	/**
	 * Determines whether this instance already has an initialized runtime.
	 */
	private _hasActiveRuntime(): boolean {
		return !!(this._iframe || this._proxy || this._messageListener)
	}

	/**
	 * Parses and validates the iframe origin from the configured API base URL.
	 *
	 * @throws Error When `apiBase` is not a valid URL.
	 */
	private _resolveIframeOrigin(apiBase: string): string {
		try {
			return new URL(apiBase).origin
		} catch {
			throw new Error(`[Char] Invalid api-base: "${apiBase}". Must be a valid URL.`)
		}
	}

	/**
	 * Creates a fully configured iframe element for the embed runtime.
	 */
	private _createIframe(apiBase: string): HTMLIFrameElement {
		const iframe = document.createElement('iframe')
		iframe.src = getIframeSrc(apiBase, window.location.origin)
		iframe.style.cssText = IFRAME_STYLE
		iframe.title = 'Char AI Assistant'
		iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms')
		iframe.setAttribute('allow', 'microphone')
		return iframe
	}

	/**
	 * Creates the message listener that filters by source/origin and dispatches typed events.
	 */
	private _createMessageListener(
		iframe: HTMLIFrameElement,
		iframeOrigin: string,
	): (event: MessageEvent) => void {
		return (event: MessageEvent) => {
			// TODO(security#2): trusted-host assumption currently defers per-origin message rate limits
			// and payload-size caps for iframe -> host postMessage handling.
			if (event.source !== iframe.contentWindow) return
			if (event.origin !== iframeOrigin) return
			if (!isCharIframeMessageData(event.data)) return
			this._handleIframeMessage(event.data, iframeOrigin)
		}
	}

	/**
	 * Routes validated iframe messages to local handlers and host DOM events.
	 */
	private _handleIframeMessage(data: CharIframeMessageData, iframeOrigin: string): void {
		switch (data.type) {
			case 'char-ready':
				this._iframeReady = true
				this._sendInitialContext(iframeOrigin)
				return
			case 'char-initialized':
				this._emitCharEvent('char-initialized')
				return
			case 'char-size-changed': {
				const width = getNumberOrUndefined(data.width)
				const height = getNumberOrUndefined(data.height)
				const rawSizeMode = getStringOrUndefined(data.displayMode)
				const sizeDisplayMode = rawSizeMode && (DISPLAY_MODES as readonly string[]).includes(rawSizeMode)
					? (rawSizeMode as CharDisplayMode)
					: undefined
				if (width !== undefined || height !== undefined) {
					const detail: CharSizeChangedDetail = { width, height, displayMode: sizeDisplayMode }
					this._emitCharEventWithDetail('char-size-changed', detail)
				}
				return
			}
			case 'char-request-display-mode': {
				const rawMode = getStringOrUndefined(data.mode)
				const mode = rawMode && (DISPLAY_MODES as readonly string[]).includes(rawMode)
					? (rawMode as CharDisplayMode)
					: undefined
				const detail: CharRequestDisplayModeDetail = { mode }
				this._emitCharEventWithDetail('char-request-display-mode', detail)
				return
			}
			case 'char-close':
				this._emitCharEvent('char-close')
				return
			case 'char-error':
				this._emitCharEventWithDetail('char-error', {
					code: getStringOrUndefined(data.code) ?? 'UNKNOWN',
					message: getStringOrUndefined(data.message),
				})
				return
			case 'char-open-link': {
				const requestId = getStringOrUndefined(data.requestId)
				const url = getStringOrUndefined(data.url)
				if (requestId && url) {
					this._handleOpenLinkRequest(requestId, url, iframeOrigin)
				} else if (requestId) {
					this._iframe?.contentWindow?.postMessage(
						{ type: 'char-open-link-result', requestId, ok: false, error: 'Missing url in char-open-link message' } satisfies CharHostToIframeMessage,
						iframeOrigin,
					)
				} else {
					console.error('[Char] Received char-open-link without requestId')
				}
				return
			}
			case 'char-teardown-complete':
				this._resolvePendingTeardown(getStringOrUndefined(data.requestId))
				return
			default:
				console.warn(`[Char] Unrecognized iframe message type: "${(data as { type?: string }).type}"`)
				return
		}
	}

	/**
	 * Emits a host-facing custom event without detail payload.
	 */
	private _emitCharEvent(type: string): void {
		this.dispatchEvent(new CustomEvent(type, CHAR_CUSTOM_EVENT_OPTIONS))
	}

	/**
	 * Emits a host-facing custom event with detail payload.
	 */
	private _emitCharEventWithDetail(type: string, detail: unknown): void {
		this.dispatchEvent(new CustomEvent(type, { ...CHAR_CUSTOM_EVENT_OPTIONS, detail }))
	}

	/**
	 * Emits a standardized `char-error` event.
	 */
	private _emitCharError(code: string, message: string): void {
		const detail: CharErrorDetail = { code, message }
		this._emitCharEventWithDetail('char-error', detail)
	}

	/**
	 * Unmount lifecycle: requests iframe teardown and releases all host resources.
	 */
	disconnectedCallback() {
		if (this._isDisconnecting) return
		this._isDisconnecting = true

		try {
			const iframeOrigin = this._getIframeOrigin()
			if (this._iframeReady && iframeOrigin) {
				this._requestTeardown(iframeOrigin, 'unmount')
					.catch((err) => console.error('[Char] Teardown failed:', err))
					.finally(() => {
						if (this.isConnected) {
							this._isDisconnecting = false
							return
						}
						this._finalizeDisconnect()
					})
				return
			}
		} catch (err) {
			console.error('[Char] Error during disconnect:', err)
		}

		this._finalizeDisconnect()
	}

	/**
	 * Releases iframe, observers, listeners, and pending teardown state.
	 */
	private _finalizeDisconnect(): void {
		if (this._proxy) {
			this._proxy.destroy()
			this._proxy = null
		}

		if (this._messageListener) {
			window.removeEventListener('message', this._messageListener)
			this._messageListener = null
		}

		if (this._darkModeObserver) {
			this._darkModeObserver.disconnect()
			this._darkModeObserver = null
		}
		if (this._darkModeMediaQuery && this._darkModeMediaHandler) {
			this._darkModeMediaQuery.removeEventListener('change', this._darkModeMediaHandler)
			this._darkModeMediaQuery = null
			this._darkModeMediaHandler = null
		}

		if (this._styleObserver) {
			this._styleObserver.disconnect()
			this._styleObserver = null
		}

		if (this._containerResizeObserver) {
			this._containerResizeObserver.disconnect()
			this._containerResizeObserver = null
		}

		if (this._iframe) {
			this._iframe.remove()
			this._iframe = null
		}

		if (this._teardownPending) {
			window.clearTimeout(this._teardownPending.timeoutId)
			this._teardownPending.resolve()
			this._teardownPending = null
		}

		this._iframeReady = false
		this._pendingAuth = null
		this._hostContext = {}
		this._teardownSequence = 0
		this._isDisconnecting = false
	}

	/**
	 * Reacts to observed attribute updates after iframe readiness.
	 * Attributes that affect iframe boot configuration are intentionally ignored
	 * after mount (`enable-debug-tools`, `api-base`).
	 */
	attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null) {
		if (!this._iframe || !this._iframeReady) return

		switch (name) {
			case 'publishable-key':
				if (newValue) {
					this._pendingAuth = { publishableKey: newValue }
					const iframeOrigin = this._getIframeOrigin()
					if (iframeOrigin) {
						this._postAuth(iframeOrigin)
					}
				}
				break
			case 'display-mode':
				this.setHostContext({
					displayMode: resolveDisplayModeFromAttribute(newValue),
				})
				break
			case 'enable-debug-tools':
			case 'api-base':
				break
		}
	}

	/**
	 * Connect to the Char agent with authentication.
	 *
	 * The token is stored as a JavaScript property (not as a DOM attribute),
	 * preventing exposure to DOM inspection and session replay tools.
	 *
	 * @param options - Authentication payload.
	 * @returns `true` when the payload is accepted; `false` when validation fails.
	 */
	connect(options: ConnectOptions): boolean {
		if (!options?.publishableKey) {
			this._emitCharError(
				'MISSING_PUBLISHABLE_KEY',
				'connect() requires publishableKey',
			)
			return false
		}

		this._pendingAuth = {
			publishableKey: options.publishableKey,
			idToken: options.idToken,
		}

		const iframeOrigin = this._getIframeOrigin()
		if (this._iframeReady && iframeOrigin) {
			this._postAuth(iframeOrigin)
		}

		return true
	}

	/**
	 * Convenience method for declarative wrappers.
	 * Applies auth when provided, otherwise clears auth.
	 *
	 * @param options - Auth payload to connect, or `null` to disconnect.
	 * @returns Result from `connect()` or `disconnect()`.
	 */
	setAuth(options: ConnectOptions | null): boolean {
		if (!options) {
			return this.disconnect()
		}
		return this.connect(options)
	}

	/**
	 * Disconnect from the Char agent.
	 * Clears pending auth state and posts a disconnect message to the iframe.
	 *
	 * @returns `true` when the disconnect message was sent; `false` when the iframe was not ready.
	 */
	disconnect(): boolean {
		this._pendingAuth = null
		const iframeOrigin = this._getIframeOrigin()
		if (this._iframeReady && this._iframe && iframeOrigin) {
			this._iframe.contentWindow?.postMessage(
				{ type: 'char-disconnect' } satisfies CharHostToIframeMessage,
				iframeOrigin,
			)
			return true
		}
		return false
	}

	/**
	 * Resolves the current iframe origin when mounted.
	 */
	private _getIframeOrigin(): string | null {
		if (!this._iframe?.src) return null
		try {
			return new URL(this._iframe.src).origin
		} catch {
			console.error('[Char] Failed to parse iframe origin from src:', this._iframe.src)
			return null
		}
	}

	/**
	 * Update the host context sent to the iframe.
	 * Only changed fields are transmitted (diffing pattern).
	 *
	 * @param hostContext - Partial host context patch to merge and emit.
	 */
	setHostContext(hostContext: CharHostContext): void {
		const changes: CharHostContext = {}
		const nextContext = mergeHostContext(this._hostContext, hostContext)

		let hasChanges = false
		for (const key of Object.keys(hostContext) as Array<keyof CharHostContext>) {
			const oldValue = this._hostContext[key]
			const newValue = nextContext[key]
			if (deepEqual(oldValue, newValue)) {
				continue
			}
			;(changes as Record<string, unknown>)[key] = hostContext[key]
			hasChanges = true
		}
		if (hasChanges) {
			this._hostContext = nextContext
			this._sendContextDelta(changes)
		}
	}

	/**
	 * Build and send the full initial context after iframe signals char-ready.
	 *
	 * @param iframeOrigin - Trusted origin used for postMessage target filtering.
	 */
	private _sendInitialContext(iframeOrigin: string): void {
		const vars = extractCharVariables(this)
		const theme = isDarkMode() ? 'dark' as const : 'light' as const
		const displayModeAttr = this.getAttribute('display-mode')
		const locale = typeof navigator !== 'undefined' ? navigator.language : undefined
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
		const containerDimensions: CharContainerDimensions = {
			width: this.clientWidth,
			height: this.clientHeight,
		}
		const safeAreaInsets = getSafeAreaInsets()

		const displayMode = resolveDisplayModeFromAttribute(displayModeAttr) ?? 'inline'

		const context: CharHostContext = {
			theme,
			styles: { variables: vars },
			displayMode,
			availableDisplayModes: [...DISPLAY_MODES],
			containerDimensions,
			locale,
			timeZone,
			platform: detectPlatform(),
			deviceCapabilities: getDeviceCapabilities(),
			safeAreaInsets,
		}

		this._hostContext = context
		if (!this._iframe?.contentWindow) {
			console.error('[Char] iframe signaled char-ready but contentWindow is null')
			this._iframeReady = false
			this._emitCharError('IFRAME_CONTENT_WINDOW_NULL', 'iframe signaled char-ready but contentWindow is null')
			return
		}
		this._iframe.contentWindow.postMessage(
			{ type: 'char-context', ...context } satisfies CharHostToIframeMessage,
			iframeOrigin
		)

		const debugAttr = this.getAttribute('enable-debug-tools')
		if (parseBooleanAttribute(debugAttr)) {
			this._iframe?.contentWindow?.postMessage(
				{ type: 'char-debug-tools', enabled: true } satisfies CharHostToIframeMessage,
				iframeOrigin
			)
		}

		if (this._pendingAuth) {
			this._postAuth(iframeOrigin)
		}
	}

	/**
	 * Send a context delta to the iframe.
	 *
	 * @param changes - Minimal context patch derived from diffing.
	 */
	private _sendContextDelta(changes: CharHostContext): void {
		if (!this._iframeReady || !this._iframe?.contentWindow) return
		const iframeOrigin = this._getIframeOrigin()
		if (!iframeOrigin) {
			console.warn('[Char] Cannot send context delta: iframe origin is null')
			return
		}
		this._iframe.contentWindow.postMessage(
			{ type: 'char-context', ...changes } satisfies CharHostToIframeMessage,
			iframeOrigin
		)
	}

	/**
	 * Post authentication credentials to the iframe.
	 *
	 * @param iframeOrigin - Trusted origin used for postMessage target filtering.
	 */
	private _postAuth(iframeOrigin: string): void {
		if (!this._pendingAuth || !this._iframe?.contentWindow) return

		if (!this._pendingAuth.publishableKey) {
			console.error('[Char] _postAuth called but _pendingAuth has no publishableKey')
			this._pendingAuth = null
			return
		}

		const message: CharHostToIframeMessage = {
			type: 'char-auth',
			publishableKey: this._pendingAuth.publishableKey,
			idToken: this._pendingAuth.idToken,
		}

		this._iframe.contentWindow.postMessage(message, iframeOrigin)
	}

	/**
	 * Requests graceful iframe teardown and resolves once acknowledged or timed out.
	 *
	 * @param iframeOrigin - Trusted origin used for postMessage target filtering.
	 * @param reason - Lifecycle reason for teardown.
	 * @returns Promise resolved when teardown flow completes.
	 */
	private _requestTeardown(
		iframeOrigin: string,
		reason: 'disconnect' | 'unmount' | 'reload',
	): Promise<void> {
		if (!this._iframeReady || !this._iframe?.contentWindow) {
			return Promise.resolve()
		}

		if (this._teardownPending) {
			window.clearTimeout(this._teardownPending.timeoutId)
			this._teardownPending.resolve()
			this._teardownPending = null
		}

		const requestId = `teardown-${Date.now()}-${++this._teardownSequence}`
		return new Promise((resolve) => {
			const timeoutId = window.setTimeout(() => {
				if (this._teardownPending?.requestId === requestId) {
					console.debug('[Char] Teardown timed out after 1000ms, requestId:', requestId)
					this._teardownPending = null
					resolve()
				}
			}, 1000)

			this._teardownPending = {
				requestId,
				timeoutId,
				resolve: () => {
					window.clearTimeout(timeoutId)
					resolve()
				},
			}

			this._iframe?.contentWindow?.postMessage(
				{
					type: 'char-teardown',
					requestId,
					reason,
				} satisfies CharHostToIframeMessage,
				iframeOrigin
			)
		})
	}

	/**
	 * Completes an in-flight teardown promise when the iframe acknowledges it.
	 *
	 * @param requestId - Optional request identifier to match against pending teardown.
	 */
	private _resolvePendingTeardown(requestId?: string): void {
		const pending = this._teardownPending
		if (!pending) return
		if (requestId && requestId !== pending.requestId) return
		this._teardownPending = null
		pending.resolve()
	}

	/**
	 * Handles open-link requests from the iframe with protocol allowlisting.
	 * This allowlist intentionally mirrors the iframe-side pre-filter.
	 *
	 * @param requestId - Link request identifier supplied by the iframe.
	 * @param url - Requested URL string.
	 * @param iframeOrigin - Trusted origin used for postMessage target filtering.
	 */
	private _handleOpenLinkRequest(requestId: string, url: string, iframeOrigin: string): void {
		// TODO(security#4): trusted-host assumption currently defers destination domain allowlisting.
		let ok = false
		let error: string | undefined
		try {
			const parsed = new URL(url, window.location.href)
			const protocol = parsed.protocol.toLowerCase()
			const isAllowedProtocol =
				protocol === 'http:' ||
				protocol === 'https:' ||
				protocol === 'mailto:' ||
				protocol === 'tel:'
			if (!isAllowedProtocol) {
				throw new Error(`Unsupported URL protocol: ${protocol}`)
			}

			const popup = window.open(parsed.toString(), '_blank', 'noopener,noreferrer')
			ok = popup !== null || protocol === 'mailto:' || protocol === 'tel:'
			if (!ok) {
				error = 'Popup blocked by browser'
			}

			this.dispatchEvent(
				new CustomEvent('char-open-link', {
					...CHAR_CUSTOM_EVENT_OPTIONS,
					detail: {
						requestId,
						url: parsed.toString(),
						ok,
					} satisfies CharOpenLinkDetail,
				})
			)
		} catch (err) {
			error = err instanceof Error ? err.message : String(err)
			console.error('[Char] open-link request failed:', err)
		}

		this._iframe?.contentWindow?.postMessage(
			{ type: 'char-open-link-result', requestId, ok, error } satisfies CharHostToIframeMessage,
			iframeOrigin
		)
	}

	/**
	 * Observe dark mode changes and forward to iframe via unified context.
	 */
	private _observeDarkMode(): void {
		const syncThemeAndStyles = () => {
			if (!this._iframeReady) return
			this.setHostContext({
				theme: isDarkMode() ? 'dark' : 'light',
				styles: { variables: extractCharVariables(this) },
			})
		}

		this._darkModeObserver = new MutationObserver(syncThemeAndStyles)
		this._darkModeObserver.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['class', 'style', 'data-theme'],
		})

		this._darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
		this._darkModeMediaHandler = syncThemeAndStyles
		this._darkModeMediaQuery.addEventListener('change', this._darkModeMediaHandler)
	}

	/**
	 * Observe style attribute changes on <char-agent> to detect CSS variable updates.
	 * Re-extracts variables and sends delta via unified context.
	 */
	private _observeStyleChanges(): void {
		this._styleObserver = new MutationObserver(() => {
			if (!this._iframeReady) return
			this.setHostContext({ styles: { variables: extractCharVariables(this) } })
		})
		this._styleObserver.observe(this, {
			attributes: true,
			attributeFilter: ['style', 'class'],
		})
	}

	/**
	 * Observe host element dimensions to provide containerDimensions context.
	 */
	private _observeContainerDimensions(): void {
		this._containerResizeObserver = new ResizeObserver((entries) => {
			if (!this._iframeReady) return
			const entry = entries[0]
			if (!entry) return
			this.setHostContext({
				containerDimensions: {
					width: Math.round(entry.contentRect.width),
					height: Math.round(entry.contentRect.height),
				},
				safeAreaInsets: getSafeAreaInsets(),
			})
		})
		this._containerResizeObserver.observe(this)
	}

	/**
	 * Resolve apiBase override from property (React 19) or attribute.
	 *
	 * @returns Sanitized API base URL without trailing slash.
	 */
	private _resolveApiBaseOverride(): string | undefined {
		const raw =
			(typeof this.apiBase === 'string' ? this.apiBase : this.getAttribute('api-base')) ??
			undefined
		const trimmed = raw?.trim()
		if (!trimmed) return undefined
		return trimmed.replace(/\/+$/, '')
	}
}

/**
 * Type declaration for the Char custom element.
 * Useful for TypeScript consumers using refs.
 *
 * @public
 */
export type { CharAgentElement }

/**
 * Register the <char-agent> custom element.
 * Called automatically when importing this module.
 *
 * @param tagName - Optional custom tag name override.
 *
 * @public
 */
export function registerChar(tagName = 'char-agent'): void {
	if (typeof window === 'undefined') return

	if (!customElements.get(tagName)) {
		customElements.define(tagName, CharAgentElement)
	}
}

registerChar()

declare global {
	interface HTMLElementTagNameMap {
		'char-agent': CharAgentElement
	}

	/* biome-ignore lint/style/noNamespace: JSX intrinsic-elements augmentation uses namespace syntax by TypeScript design. */
	namespace JSX {
		interface IntrinsicElements {
			'char-agent': {
				/** Publishable key for org identification. */
				'publishable-key'?: string
				/** Property form of publishable key. */
				publishableKey?: string
				/** Explicit API base override, e.g. `https://staging-app.usechar.ai`. */
				'api-base'?: string
				/** Property form of API base override. */
				apiBase?: string
				/** Enable debug tools for inspecting embedded agent internal state. */
				'enable-debug-tools'?: boolean
				/** Display mode for host orchestration: `pip` is collapsed launcher mode. */
				'display-mode'?: CharDisplayMode
				/** Callback when the close button inside the agent panel is clicked. */
				onClose?: () => void
				/** Imperative ref to the custom element instance. */
				ref?: unknown
				[key: string]: unknown
			}
		}
	}
}

declare module 'react/jsx-runtime' {
	/* biome-ignore lint/style/noNamespace: JSX intrinsic-elements augmentation uses namespace syntax by TypeScript design. */
	namespace JSX {
		interface IntrinsicElements {
			'char-agent': {
				'publishable-key'?: string
				publishableKey?: string
				'api-base'?: string
				apiBase?: string
				'enable-debug-tools'?: boolean
				'display-mode'?: CharDisplayMode
				onClose?: () => void
				ref?: unknown
				[key: string]: unknown
			}
		}
	}
}
